import { useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  Loader2,
  RefreshCw,
  Search,
  XCircle,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSeoUrlInspection, type UrlInspectionResult } from "@/hooks/useSeoUrlInspection";

const humanize = (value: string | null) => {
  if (!value) return "—";
  return value
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
};

const formatDateTime = (value: string | null) =>
  value
    ? new Date(value).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "Never crawled";

function verdictBadge(result: UrlInspectionResult) {
  if (result.error) {
    return (
      <Badge variant="outline" className="gap-1">
        <AlertCircle className="h-3 w-3" /> Check failed
      </Badge>
    );
  }
  if (result.verdict === "PASS") {
    return (
      <Badge className="gap-1 bg-primary/15 text-primary hover:bg-primary/20">
        <CheckCircle2 className="h-3 w-3" /> Indexed
      </Badge>
    );
  }
  if (result.verdict === "FAIL") {
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="h-3 w-3" /> Not indexed
      </Badge>
    );
  }
  if (result.verdict === "NEUTRAL" || result.verdict === "PARTIAL") {
    return <Badge variant="secondary">Needs review</Badge>;
  }
  return <Badge variant="outline">Unknown</Badge>;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="break-words text-sm">{value}</p>
    </div>
  );
}

function CrawlDetails({ result }: { result: UrlInspectionResult }) {
  if (result.error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Could not inspect this URL</AlertTitle>
        <AlertDescription className="break-words">{result.error}</AlertDescription>
      </Alert>
    );
  }

  const canonicalMismatch =
    result.googleCanonical && result.userCanonical && result.googleCanonical !== result.userCanonical;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <DetailRow label="Coverage" value={humanize(result.coverageState)} />
        <DetailRow label="Last crawled" value={formatDateTime(result.lastCrawlTime)} />
        <DetailRow label="Crawled as" value={humanize(result.crawledAs)} />
        <DetailRow label="Page fetch" value={humanize(result.pageFetchState)} />
        <DetailRow label="Indexing allowed" value={humanize(result.indexingState)} />
        <DetailRow label="robots.txt" value={humanize(result.robotsTxtState)} />
        <DetailRow label="Google-selected canonical" value={result.googleCanonical ?? "—"} />
        <DetailRow label="Declared canonical" value={result.userCanonical ?? "—"} />
        <DetailRow
          label="Mobile usability"
          value={humanize(result.mobileVerdict)}
        />
      </div>

      {canonicalMismatch ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Canonical mismatch</AlertTitle>
          <AlertDescription>
            Google chose a different canonical than the one this page declares, so ranking signals are
            being consolidated elsewhere.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <DetailRow
          label="Discovered via sitemaps"
          value={result.sitemaps.length ? result.sitemaps.join(", ") : "Not found in a submitted sitemap"}
        />
        <DetailRow
          label="Referring pages"
          value={
            result.referringUrls.length
              ? `${result.referringUrls.length} internal link${result.referringUrls.length === 1 ? "" : "s"}`
              : "No referring pages reported"
          }
        />
      </div>

      <Button asChild variant="outline" size="sm">
        <a href={result.url} target="_blank" rel="noreferrer">
          Open page <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
        </a>
      </Button>
    </div>
  );
}

function ResultRow({ result }: { result: UrlInspectionResult }) {
  const [open, setOpen] = useState(false);
  let path = result.url;
  try {
    path = new URL(result.url).pathname || "/";
  } catch {
    /* keep raw url */
  }

  return (
    <>
      <TableRow className="cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <TableCell className="w-8">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-expanded={open}
            aria-label={`Toggle crawl details for ${path}`}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </Button>
        </TableCell>
        <TableCell className="max-w-[280px] truncate font-medium">{path}</TableCell>
        <TableCell>{verdictBadge(result)}</TableCell>
        <TableCell className="hidden text-sm text-muted-foreground md:table-cell">
          {humanize(result.coverageState)}
        </TableCell>
        <TableCell className="hidden text-sm text-muted-foreground lg:table-cell">
          {formatDateTime(result.lastCrawlTime)}
        </TableCell>
      </TableRow>
      {open ? (
        <TableRow className="bg-muted/40 hover:bg-muted/40">
          <TableCell colSpan={5} className="p-6">
            <CrawlDetails result={result} />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}


export function SeoUrlDrilldownCard() {
  const { status, report, candidates, error, run } = useSeoUrlInspection();
  const [filter, setFilter] = useState<"all" | "indexed" | "problem">("all");

  const results = report?.results ?? [];
  const visible = results.filter((r) => {
    if (filter === "indexed") return r.verdict === "PASS";
    if (filter === "problem") return r.verdict !== "PASS";
    return true;
  });

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" /> URL-level indexing
          </CardTitle>
          <CardDescription>
            Per-URL indexing verdicts and crawl details from Google's index, pulled from the URLs in your
            sitemap. Expand a row for coverage, canonical, fetch, and crawl state.
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void run()}
          disabled={status === "loading"}
        >
          {status === "loading" ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-1.5 h-4 w-4" />
          )}
          {status === "idle" ? "Run URL check" : "Re-check URLs"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {status === "idle" ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Run a check to inspect each sitemap URL against Google's index. This calls Search Console once
            per URL, so it takes a few seconds.
          </p>
        ) : null}

        {status === "loading" ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : null}

        {status === "error" ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Could not inspect URLs</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {status === "selection_required" ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Choose a Search Console property</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>Several verified properties cover this site. Pick the one to inspect against.</p>
              <div className="flex flex-wrap gap-2">
                {candidates.map((candidate) => (
                  <Button
                    key={candidate}
                    size="sm"
                    variant="outline"
                    onClick={() => void run({ selectedSiteUrl: candidate })}
                  >
                    {candidate}
                  </Button>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        ) : null}

        {status === "ready" && report ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">{results.length} URLs checked</Badge>
              <Badge className="bg-primary/15 text-primary hover:bg-primary/20">
                {report.summary.indexed} indexed
              </Badge>
              {report.summary.notIndexed > 0 ? (
                <Badge variant="destructive">{report.summary.notIndexed} not indexed</Badge>
              ) : null}
              {report.summary.other > 0 ? (
                <Badge variant="secondary">{report.summary.other} needs review</Badge>
              ) : null}
              {report.summary.errors > 0 ? (
                <Badge variant="outline">{report.summary.errors} checks failed</Badge>
              ) : null}
              <span className="ml-auto text-xs text-muted-foreground">
                Checked {formatDateTime(report.checkedAt)}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {(["all", "problem", "indexed"] as const).map((option) => (
                <Button
                  key={option}
                  size="sm"
                  variant={filter === option ? "default" : "outline"}
                  onClick={() => setFilter(option)}
                >
                  {option === "all" ? "All" : option === "problem" ? "Needs attention" : "Indexed"}
                </Button>
              ))}
            </div>

            {report.truncated ? (
              <p className="text-xs text-muted-foreground">
                Showing the first {report.maxUrls} sitemap URLs to stay within Search Console quota.
              </p>
            ) : null}

            {visible.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No URLs match this filter.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>URL</TableHead>
                    <TableHead>Verdict</TableHead>
                    <TableHead className="hidden md:table-cell">Coverage</TableHead>
                    <TableHead className="hidden lg:table-cell">Last crawled</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((result) => (
                    <ResultRow key={result.url} result={result} />
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default SeoUrlDrilldownCard;
